export interface ShapeVariant {
  [key: string]: number | string | boolean
}

export interface ShapeFamily {
  id: string
  baseLabel: string
  representativeType: string
  variants: ShapeVariant[]
}

export const MAIN_SHAPES: ShapeFamily[] = [
  {
    id: 'au-full',
    baseLabel: 'A-Unit Full',
    representativeType: 'AUFULLMANYPILLOWSQ',
    variants: [
      { seat_sections: 2, backrest_sections: 2, width: 140, armrest_width: 10 },
      { extension_type: 'STANDART', extendable_part_length: 60 },
      { extension_type: 'ELECTRIC_RECLINER', extendable_part_length: 60 },
      { backrest_type: 'MECHANIC' },
      { covered_side: true },
    ],
  },
  {
    id: 'element',
    baseLabel: 'Element',
    representativeType: 'EMANYPILLOWSQ',
    variants: [
      { seat_sections: 2, backrest_sections: 2, width: 140 },
      { extension_type: 'STANDART', extendable_part_length: 60 },
      { extension_type: 'ELECTRIC_RECLINER', extendable_part_length: 60 },
      { backrest_type: 'MECHANIC' },
    ],
  },
  {
    id: 'element-l',
    baseLabel: 'Element Left',
    representativeType: 'EMANYPILLOWSQL',
    variants: [
      { seat_sections: 2, backrest_sections: 2, width: 140 },
      { extension_type: 'ELECTRIC_RECLINER', extendable_part_length: 60 },
      { covered_side: true },
    ],
  },
  {
    id: 'element-r',
    baseLabel: 'Element Right',
    representativeType: 'EMANYPILLOWSQR',
    variants: [
      { seat_sections: 2, backrest_sections: 2, width: 140 },
      { extension_type: 'ELECTRIC_RECLINER', extendable_part_length: 60 },
      { covered_side: true },
    ],
  },
  {
    id: 'chaise-outer-l',
    baseLabel: 'Chaise Outer Left',
    representativeType: 'LCHOUTERMANYPILLOWSL',
    variants: [
      { seat_sections: 2, backrest_sections: 2, width: 140 },
      { corner_radius: 90 },
      { backrest_type: 'MECHANIC' },
    ],
  },
  {
    id: 'chaise-outer-r',
    baseLabel: 'Chaise Outer Right',
    representativeType: 'LCHOUTERMANYPILLOWSR',
    variants: [
      { seat_sections: 2, backrest_sections: 2, width: 140 },
      { corner_radius: 90 },
      { backrest_type: 'MECHANIC' },
    ],
  },
  {
    id: 'chaise-outer',
    baseLabel: 'Chaise Outer',
    representativeType: 'LCHOUTERMANYPILLOW',
    variants: [{ extension_type: 'STANDART' }],
  },

  {
    id: 'ottoman-p-l',
    baseLabel: 'Ottoman P Left',
    representativeType: 'OTTP1L',
    variants: [{ corner_radius: 100 }],
  },
  {
    id: 'ottoman-p-r',
    baseLabel: 'Ottoman P Right',
    representativeType: 'OTTP1R',
    variants: [{ corner_radius: 100 }],
  },
  {
    id: 'ottoman-kc-l',
    baseLabel: 'Ottoman KC Left',
    representativeType: 'OTKCMANYPILLOWL',
    variants: [
      { corner_part_length: 90 },
      { corner_radius: 90 },
      { extension_type: 'STANDART' },
    ],
  },
  {
    id: 'ottoman-kc-r',
    baseLabel: 'Ottoman KC Right',
    representativeType: 'OTKCMANYPILLOWR',
    variants: [
      { corner_part_length: 90 },
      { corner_radius: 90 },
      { extension_type: 'STANDART' },
    ],
  },
  {
    id: 'corner-cut',
    baseLabel: 'Corner',
    representativeType: 'CORNERCUTL',
    variants: [
      { corner_part_length: 90 },
      { corner_part_length: 90, number_of_big_pillows: 2 },
    ],
  },
  {
    id: 'angle-corner-lch-l',
    baseLabel: 'Angle Corner LCH Left',
    representativeType: 'ANGLECORNERLCHL',
    variants: [{ corner_part_length: 90, angle: 30 }],
  },
  {
    id: 'angle-corner-lch-r',
    baseLabel: 'Angle Corner LCH Right',
    representativeType: 'ANGLECORNERLCHR',
    variants: [{ corner_part_length: 90, angle: 30 }],
  },
  {
    id: 'angle-corner',
    baseLabel: 'Angle Corner',
    representativeType: 'ANGLECORNER',
    variants: [{ angle: 30 }],
  },
  {
    id: 'angle-corner-flat',
    baseLabel: 'Angle Corner Flat',
    representativeType: 'ANGLECORNERFLAT',
    variants: [{ angle: -100, corner_part_length: 80 }],
  },
  {
    id: 'arch-corner',
    baseLabel: 'Arch Corner',
    representativeType: 'ARCHCORNERL',
    variants: [
      { seat_sections: 2, backrest_sections: 2, corner_radius: 60 },
      {
        corner_radius: 91,
        angle: 45,
        seat_sections: 1,
        backrest_sections: 1,
        number_of_big_pillows: 1,
      },
    ],
  },
  {
    id: 'composite',
    baseLabel: 'Composite',
    representativeType: 'COMPOSITE',
    variants: [{ corner_part_length: 90 }, { extension_type: 'STANDART' }],
  },
  {
    id: 'round',
    baseLabel: 'Round',
    representativeType: 'ROUND',
    variants: [{ width: 100, height: 100 }],
  },
  {
    id: 'puf',
    baseLabel: 'Pouf',
    representativeType: 'PUF',
    variants: [{ width: 100, height: 100 }],
  },
]

// Helper: human-readable labels for dimension fields
export const VARIANT_LABELS: Record<string, string> = {
  seat_sections: 'Seat sections',
  backrest_sections: 'Backrest sections',
  extension_type: 'Extension',
  extendable_part_length: 'Extendable',
  corner_part_length: 'Corner part',
  corner_radius: 'Round corners',
  angle: 'Angle',
  covered_side: 'Covered side',
  backrest_type: 'Backrest type',
}

// Helper: humanize enum string values
const VALUE_LABELS: Record<string, string> = {
  STANDART: 'Standard',
  ELECTRIC_RECLINER: 'Electric recliner',
  MECHANIC: 'Mechanic',
}

// Helper: format one variant object into a readable string
export function formatVariantLine(variant: ShapeVariant): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(variant)) {
    const label = VARIANT_LABELS[key]
    if (!label) continue
    if (typeof value === 'boolean') {
      parts.push(label)
    } else if (typeof value === 'string') {
      parts.push(`${label}: ${VALUE_LABELS[value] || value}`)
    } else {
      // Numbers — add unit hints for specific keys
      const unit = [
        'corner_radius',
        'corner_part_length',
        'extendable_part_length',
      ].includes(key)
        ? 'cm'
        : key === 'angle'
          ? '°'
          : ''
      parts.push(`${label}: ${value}${unit}`)
    }
  }
  return parts.join(', ')
}

// Helper: convert a snake_case ShapeVariant into camelCase props for Konva components
export function variantToProps(
  variant: ShapeVariant
): Record<string, number | string | boolean> {
  const props: Record<string, number | string | boolean> = {}
  for (const [key, value] of Object.entries(variant)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    )
    props[camelKey] = value
  }
  return props
}

// Helper: get all main shape representativeTypes
export function getMainShapeTypes(): Set<string> {
  return new Set(MAIN_SHAPES.map(s => s.representativeType))
}
